import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';

import type { TicketType } from '../../api/tickets/types';

import { useReportIssue } from './ReportIssueContext';

const OPTIONS: Array<{ type: TicketType; label: string; icon: React.ReactNode }> = [
    { type: 'bug', label: 'Report an Issue', icon: <BugReportOutlinedIcon fontSize="small" /> },
    {
        type: 'improvement',
        label: 'Suggest an Improvement',
        icon: <LightbulbOutlinedIcon fontSize="small" />
    },
    { type: 'feature', label: 'Feature Request', icon: <StarBorderIcon fontSize="small" /> }
];

/**
 * "Feedback ▾" menu in the conventional top-right help/feedback slot, immediately left of the avatar —
 * where Jira, Slack and Google Workspace put theirs.
 *
 * Labelled rather than icon-only on desktop: there is no universally recognised glyph for "report an
 * issue" (Microsoft labels it, Atlassian hides it behind `?`, a bug icon reads as developer-only), so a
 * lone icon in a row of five others simply is not found. Below `md` the bar has no room for the label
 * and it degrades to an icon with a tooltip.
 *
 * Picking the type here rather than inside the dialog means the user states their intent in one click
 * and the form opens already scoped to it.
 */
const ReportIssueHeaderButton: React.FC = () => {
    const { openReportIssue } = useReportIssue();
    const history = useHistory();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);
    const handleClose = useCallback(() => setAnchorEl(null), []);

    const openMyTickets = useCallback(() => {
        setAnchorEl(null);
        history.push('/my-tickets');
    }, [history]);

    const handleSelect = useCallback(
        (type: TicketType) => {
            setAnchorEl(null);
            openReportIssue(type);
        },
        [openReportIssue]
    );

    return (
        <>
            {isMdUp ? (
                <Button
                        color="inherit"
                        onClick={handleOpen}
                        startIcon={<FeedbackOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={
                            <KeyboardArrowDownIcon
                                sx={{
                                    fontSize: 18,
                                    transition: 'transform 180ms ease',
                                    transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)'
                                }}
                            />
                        }
                        aria-label="Feedback"
                        aria-haspopup="menu"
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            borderRadius: '999px',
                            px: 1.5,
                            py: 0.5,
                            minWidth: 0,
                            border: '1px solid',
                            // Outlined against the coloured AppBar so it reads as an affordance rather than
                            // another passive status glyph, without competing with the primary blue.
                            borderColor: 'rgba(255,255,255,0.28)',
                            '&:hover': {
                                borderColor: 'rgba(255,255,255,0.55)',
                                backgroundColor: 'rgba(255,255,255,0.10)'
                            }
                        }}
                    >
                        Feedback
                    </Button>
            ) : (
                <Tooltip title="Feedback" placement="bottom">
                    <IconButton
                        color="inherit"
                        onClick={handleOpen}
                        aria-label="Feedback"
                        aria-haspopup="menu"
                        size="medium"
                    >
                        <FeedbackOutlinedIcon />
                    </IconButton>
                </Tooltip>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    elevation: 0,
                    sx: (t) => ({
                        mt: 0.75,
                        minWidth: 240,
                        borderRadius: '12px',
                        border: t.palette.mode === 'dark' ? `1px solid ${t.palette.divider}` : 'none',
                        boxShadow:
                            t.palette.mode === 'dark'
                                ? '0 10px 30px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.4)'
                                : '0 10px 30px rgba(15,23,42,0.10), 0 2px 8px rgba(15,23,42,0.06)'
                    })
                }}
            >
                <MenuItem
                    onClick={openMyTickets}
                    sx={{ mx: 1, px: 1.25, minHeight: 40, borderRadius: '8px' }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                        <ConfirmationNumberOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="My Tickets"
                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                    />
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                {OPTIONS.map((option) => (
                    <MenuItem
                        key={option.type}
                        onClick={() => handleSelect(option.type)}
                        sx={{ mx: 1, px: 1.25, minHeight: 40, borderRadius: '8px' }}
                    >
                        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                            {option.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={option.label}
                            primaryTypographyProps={{ fontSize: '0.85rem' }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ReportIssueHeaderButton;
